// frontend/src/pages/VideoAnalysis.tsx
// Multi-video analysis page. Users can load any number of MP4s into a
// flexible grid of independent slots with optional synchronized playback.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  AddCircleOutline as AddIcon,
  VideoLibrary as VideoLibraryIcon,
} from '@mui/icons-material';
import VideoSlot from '../components/VideoSlot';
import {
  AnalysisState,
  SlotState,
  consumeAnalysisQueue,
  loadAnalysisState,
  saveAnalysisState,
} from '../services/analysisSession';

const SPEED_OPTIONS = [0.5, 1, 1.5, 2, 3, 4, 6, 8, 10, 12];
const rocheBlue = '#0B41CD';

const newSlot = (): SlotState => ({
  id: crypto.randomUUID(),
  key: null,
  speedOverride: null,
});

const VideoAnalysis: React.FC = () => {
  const [state, setState] = useState<AnalysisState>(() => {
    const loaded = loadAnalysisState();
    return loaded;
  });

  // Map of slotId → video element, used for sync
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  // Guard against re-entrant sync events
  const isSyncing = useRef(false);

  // On mount: consume any queued video from "Add to Analysis" on other pages
  useEffect(() => {
    const queuedKey = consumeAnalysisQueue();
    if (queuedKey) {
      setState((prev) => {
        // If there's an empty slot, fill it; otherwise append a new one
        const emptyIdx = prev.slots.findIndex((s) => s.key === null);
        if (emptyIdx !== -1) {
          const slots = prev.slots.map((s, i) =>
            i === emptyIdx ? { ...s, key: queuedKey } : s
          );
          return { ...prev, slots };
        }
        return {
          ...prev,
          slots: [...prev.slots, { id: crypto.randomUUID(), key: queuedKey, speedOverride: null }],
        };
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist state to sessionStorage on every change
  useEffect(() => {
    saveAnalysisState(state);
  }, [state]);

  // Apply global speed to all slots that have no override
  useEffect(() => {
    videoRefs.current.forEach((el, id) => {
      const slot = state.slots.find((s) => s.id === id);
      if (slot && slot.speedOverride === null) {
        el.playbackRate = state.globalSpeed;
      }
    });
  }, [state.globalSpeed, state.slots]);

  const handleAddSlot = () => {
    setState((prev) => ({ ...prev, slots: [...prev.slots, newSlot()] }));
  };

  const handleRemoveSlot = useCallback((id: string) => {
    videoRefs.current.delete(id);
    setState((prev) => ({
      ...prev,
      slots: prev.slots.filter((s) => s.id !== id),
    }));
  }, []);

  const handleVideoChange = useCallback((id: string, key: string | null) => {
    setState((prev) => ({
      ...prev,
      slots: prev.slots.map((s) => (s.id === id ? { ...s, key } : s)),
    }));
  }, []);

  const handleSpeedOverride = useCallback((id: string, speed: number | null) => {
    setState((prev) => ({
      ...prev,
      slots: prev.slots.map((s) => (s.id === id ? { ...s, speedOverride: speed } : s)),
    }));
    // Apply immediately to the video element
    const el = videoRefs.current.get(id);
    if (el) {
      el.playbackRate = speed ?? state.globalSpeed;
    }
  }, [state.globalSpeed]);

  const handleGlobalSpeedChange = (_: React.MouseEvent<HTMLElement>, value: number | null) => {
    if (value === null) return;
    // Clear all per-slot overrides and apply new global speed
    setState((prev) => ({
      ...prev,
      globalSpeed: value,
      slots: prev.slots.map((s) => ({ ...s, speedOverride: null })),
    }));
    videoRefs.current.forEach((el) => {
      el.playbackRate = value;
    });
  };

  const handleColumnsChange = (_: React.MouseEvent<HTMLElement>, value: number | null) => {
    if (value === null) return;
    setState((prev) => ({ ...prev, columns: value }));
  };

  const handleSyncToggle = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setState((prev) => ({ ...prev, syncEnabled: checked }));
  };

  const registerVideo = useCallback((id: string, el: HTMLVideoElement | null) => {
    if (el) {
      videoRefs.current.set(id, el);
    } else {
      videoRefs.current.delete(id);
    }
  }, []);

  const handleSyncEvent = useCallback(
    (type: 'play' | 'pause' | 'seek', time?: number) => {
      if (!state.syncEnabled || isSyncing.current) return;
      isSyncing.current = true;
      videoRefs.current.forEach((el) => {
        try {
          if (type === 'play') el.play().catch(() => {});
          else if (type === 'pause') el.pause();
          else if (type === 'seek' && time !== undefined) el.currentTime = time;
        } catch {
          // Ignore errors on individual elements
        }
      });
      // Release guard after a short delay to avoid feedback loops
      setTimeout(() => { isSyncing.current = false; }, 100);
    },
    [state.syncEnabled]
  );

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <VideoLibraryIcon sx={{ color: rocheBlue, fontSize: 28 }} />
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Video Analysis
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Load multiple videos side-by-side. Search for any MP4 in each slot, control playback speed per slot or globally, and optionally sync playback across all slots.
      </Typography>

      {/* Sticky toolbar */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: 2,
          px: 2,
          py: 1.25,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          backgroundColor: 'background.paper',
        }}
      >
        {/* Add slot */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddSlot}
          size="small"
          sx={{ backgroundColor: rocheBlue, '&:hover': { backgroundColor: '#0935a8' } }}
        >
          Add Video
        </Button>

        <Divider orientation="vertical" flexItem />

        {/* Column selector */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            Columns:
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={state.columns}
            onChange={handleColumnsChange}
          >
            {[1, 2, 3, 4].map((n) => (
              <ToggleButton
                key={n}
                value={n}
                sx={{
                  px: 1.25,
                  py: 0.25,
                  fontSize: '0.75rem',
                  '&.Mui-selected': {
                    backgroundColor: rocheBlue,
                    color: '#fff',
                    '&:hover': { backgroundColor: rocheBlue },
                  },
                }}
              >
                {n}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Sync toggle */}
        <Tooltip title="When on, play/pause/seek on any slot propagates to all others">
          <FormControlLabel
            control={
              <Switch
                checked={state.syncEnabled}
                onChange={handleSyncToggle}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: rocheBlue },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: rocheBlue },
                }}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                Sync playback
              </Typography>
            }
            sx={{ m: 0 }}
          />
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        {/* Global speed */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            Global speed:
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={state.globalSpeed}
            onChange={handleGlobalSpeedChange}
            sx={{ flexWrap: 'wrap', gap: 0.25 }}
          >
            {SPEED_OPTIONS.map((s) => (
              <ToggleButton
                key={s}
                value={s}
                sx={{
                  px: 0.75,
                  py: 0.25,
                  fontSize: '0.7rem',
                  minWidth: 36,
                  border: '1px solid rgba(0,0,0,0.15) !important',
                  '&.Mui-selected': {
                    backgroundColor: rocheBlue,
                    color: '#fff',
                    '&:hover': { backgroundColor: rocheBlue },
                  },
                }}
              >
                {s}×
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* Video grid */}
      {state.slots.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            border: '2px dashed rgba(0,0,0,0.15)',
            borderRadius: 2,
            p: 6,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <VideoLibraryIcon sx={{ fontSize: 56, opacity: 0.25, mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            No video slots yet
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Click <strong>Add Video</strong> to create your first slot.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddSlot}
            sx={{ backgroundColor: rocheBlue, '&:hover': { backgroundColor: '#0935a8' } }}
          >
            Add Video
          </Button>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: `repeat(${Math.min(state.columns, 2)}, 1fr)`,
              md: `repeat(${state.columns}, 1fr)`,
            },
            gap: 2,
          }}
        >
          {state.slots.map((slot) => (
            <VideoSlot
              key={slot.id}
              slot={slot}
              globalSpeed={state.globalSpeed}
              syncEnabled={state.syncEnabled}
              onRemove={handleRemoveSlot}
              onVideoChange={handleVideoChange}
              onSpeedOverride={handleSpeedOverride}
              registerVideo={registerVideo}
              onSyncEvent={handleSyncEvent}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default VideoAnalysis;
